import {
  AGENTIC_SETTINGS_CHANGED_EVENT,
  AGENTIC_SNAPSHOT_REFRESHED_EVENT,
  loadRobinhoodRefreshContext,
  type RobinhoodRefreshContext,
} from "./agenticRefreshSettings";
import { isRobinhoodIntegrationEnabled } from "./agenticFeatures";
import { isUsEquityRegularSessionOpen } from "../shared/marketHours";
import { currentSessionFeedSource, type PositionsFeedSource } from "./optionsPositionsFeed";
import {
  computeRhSyncCountdown,
  flashRhRefreshCountdownRing,
  rhRefreshCountdownRingHtml,
  rhRefreshCountdownTip,
  updateRhRefreshCountdownRing,
  type RhSyncCountdown,
} from "./rhRefreshCountdownRing";
import { getRobinhoodStatusResult } from "./robinhoodStatus";
import { peekPositionsSnapshot } from "./watchlistData";

export const HEADER_FEED_QUOTE_CACHE_MS = 60_000;
export const HEADER_FEED_QUOTE_REFRESHED_EVENT = "sfe:header-feed-quote-refreshed";

let lastQuoteFetchAt = 0;
let rhSnapshotRefreshedAt: string | null = null;
let rhSyncEnabled = true;
let rhSyncIntervalMinutes = 7;

function formatExpiresIn(sec: number): string {
  if (sec >= 3600) return `${Math.ceil(sec / 3600)}h`;
  if (sec >= 60) return `${Math.ceil(sec / 60)}m`;
  return `${Math.max(0, sec)}s`;
}

function sfRefreshCountdownTip(quoteRemainingSec: number): string {
  const quoteEvery = Math.max(1, Math.round(HEADER_FEED_QUOTE_CACHE_MS / 60_000));
  return `StockForge feed · live quotes every ${quoteEvery}m · next in ${formatExpiresIn(quoteRemainingSec)}`;
}

function currentRhSyncCountdown(): RhSyncCountdown {
  return computeRhSyncCountdown(rhSnapshotRefreshedAt, rhSyncIntervalMinutes, rhSyncEnabled);
}

function resolveActiveFeed(): PositionsFeedSource {
  if (isUsEquityRegularSessionOpen()) return "stockforge";
  if (isRobinhoodIntegrationEnabled()) return "robinhood";
  return currentSessionFeedSource();
}

function rhFeedConnected(): boolean {
  return getRobinhoodStatusResult().connected;
}

function applyRhRefreshContext(ctx: RobinhoodRefreshContext): boolean {
  const prevRefreshedAt = rhSnapshotRefreshedAt;
  rhSyncEnabled = ctx.settings.snapshotAutoRefreshEnabled;
  rhSyncIntervalMinutes = Math.max(1, ctx.settings.snapshotAutoRefreshMinutes);
  rhSnapshotRefreshedAt = ctx.snapshotRefreshedAt;
  return Boolean(prevRefreshedAt && ctx.snapshotRefreshedAt && prevRefreshedAt !== ctx.snapshotRefreshedAt);
}

function resolveQuoteFetchAt(): number {
  if (lastQuoteFetchAt > 0) return lastQuoteFetchAt;
  const peek = peekPositionsSnapshot();
  return peek?.fetchedAt ?? 0;
}

export function notifyHeaderFeedQuoteRefreshed(fetchedAt = Date.now()): void {
  lastQuoteFetchAt = fetchedAt;
  window.dispatchEvent(
    new CustomEvent(HEADER_FEED_QUOTE_REFRESHED_EVENT, { detail: { fetchedAt } }),
  );
}

export function headerFeedIndicatorHtml(): string {
  const sync = currentRhSyncCountdown();
  const ring = rhRefreshCountdownRingHtml({ sync });
  return `
    <div class="sfe-header-feed-indicator" data-sfe-header-feed hidden>
      <button type="button" class="sfe-header-feed-btn" data-action="header-feed" hidden aria-label="Positions feed status">
        <span class="sfe-header-feed-ring-host" data-header-feed-ring title="" aria-label="">${ring}</span>
        <span class="sfe-header-feed-badge" data-header-feed-badge></span>
      </button>
    </div>`;
}

export function refreshHeaderFeedIndicator(host: ParentNode): void {
  applyHeaderFeedIndicator(host);
}

function applyHeaderFeedIndicator(host: ParentNode): void {
  const wrap = host.querySelector<HTMLElement>("[data-sfe-header-feed]");
  const btn = host.querySelector<HTMLElement>("[data-action='header-feed']");
  const badge = host.querySelector<HTMLElement>("[data-header-feed-badge]");
  const ringHost = host.querySelector<HTMLElement>("[data-header-feed-ring]");
  if (!wrap || !btn || !badge || !ringHost) return;

  const feed = resolveActiveFeed();
  const rhConnected = rhFeedConnected();
  const showRh = feed === "robinhood" && isRobinhoodIntegrationEnabled();
  const showSf = feed === "stockforge";
  const visible = showRh || showSf;

  wrap.hidden = !visible;
  btn.hidden = !visible;
  wrap.dataset.feed = feed;
  wrap.classList.toggle("is-rh-disconnected", showRh && !rhConnected);
  if (showRh) wrap.dataset.rhConnected = rhConnected ? "true" : "false";
  else delete wrap.dataset.rhConnected;

  if (!visible) return;

  if (showRh) {
    badge.textContent = "Robinhood feed";
    badge.title = rhConnected
      ? "Market is closed — open positions are from your latest Robinhood sync"
      : "Market is closed — connect Robinhood to use the after-hours feed";
  } else {
    badge.textContent = "StockForge feed";
    badge.title = "Regular session — open positions and live quotes from StockForge";
  }

  const quoteFetchAt = resolveQuoteFetchAt();
  const quoteRemaining = quoteFetchAt
    ? Math.max(0, Math.ceil((quoteFetchAt + HEADER_FEED_QUOTE_CACHE_MS - Date.now()) / 1000))
    : 0;

  if (showRh && !rhConnected) {
    const tip = "Robinhood not connected — tap to connect for the after-hours positions feed";
    const sync: RhSyncCountdown = {
      remainingSec: 0,
      totalSec: 1,
      enabled: false,
      intervalMinutes: rhSyncIntervalMinutes,
    };
    updateRhRefreshCountdownRing(ringHost, {
      sync,
      quoteRemainingSec: 0,
      quoteIntervalSec: HEADER_FEED_QUOTE_CACHE_MS / 1000,
    });
    btn.title = tip;
    btn.setAttribute("data-tip", tip);
    btn.setAttribute("aria-label", tip);
  } else if (showRh) {
    const sync = currentRhSyncCountdown();
    const tip = rhRefreshCountdownTip({
      sync,
      quoteRemainingSec: quoteRemaining,
      quoteIntervalSec: HEADER_FEED_QUOTE_CACHE_MS / 1000,
    });
    updateRhRefreshCountdownRing(ringHost, {
      sync,
      quoteRemainingSec: quoteRemaining,
      quoteIntervalSec: HEADER_FEED_QUOTE_CACHE_MS / 1000,
    });
    btn.title = tip;
    btn.setAttribute("data-tip", tip);
    btn.setAttribute("aria-label", tip);
  } else {
    const tip = sfRefreshCountdownTip(quoteRemaining);
    const pseudoSync: RhSyncCountdown = {
      remainingSec: quoteRemaining,
      totalSec: HEADER_FEED_QUOTE_CACHE_MS / 1000,
      enabled: true,
      intervalMinutes: 1,
    };
    updateRhRefreshCountdownRing(ringHost, {
      sync: pseudoSync,
      quoteRemainingSec: quoteRemaining,
      quoteIntervalSec: HEADER_FEED_QUOTE_CACHE_MS / 1000,
    });
    ringHost.title = tip;
    ringHost.setAttribute("aria-label", tip);
    btn.title = tip;
    btn.setAttribute("data-tip", tip);
    btn.setAttribute("aria-label", tip);
  }
}

export function startHeaderFeedIndicator(
  root: ParentNode,
  opts: {
    onQuoteRefreshDue?: () => void;
    onRhSnapshotPoll?: () => void;
    onRhSyncDue?: () => void;
    onOpenImport?: () => void;
  } = {},
): () => void {
  const host =
    root.querySelector<HTMLElement>("[data-sfe-header-feed]")?.closest(".sfe-web-shell") ?? root;

  const onFeedClick = (event: Event) => {
    const target = event.target as HTMLElement | null;
    const btn = target?.closest?.("[data-action='header-feed']");
    if (!btn || !host.contains(btn)) return;
    if (resolveActiveFeed() === "robinhood") opts.onOpenImport?.();
  };
  host.addEventListener("click", onFeedClick);

  let quoteRefreshQueued = false;
  let pollQueued = false;
  let lastPollAt = 0;
  let lastSyncRemainingSec: number | null = null;
  let rhPollTimer: ReturnType<typeof setInterval> | null = null;

  const pollRhContext = (): void => {
    if (!isRobinhoodIntegrationEnabled()) return;
    void loadRobinhoodRefreshContext()
      .then((ctx) => {
        const updated = applyRhRefreshContext(ctx);
        if (updated) {
          const ringHost = host.querySelector<HTMLElement>("[data-header-feed-ring]");
          if (ringHost) flashRhRefreshCountdownRing(ringHost);
          opts.onRhSyncDue?.();
        }
        applyHeaderFeedIndicator(host);
      })
      .catch(() => {
        applyHeaderFeedIndicator(host);
      });
  };

  const onQuoteEvent = (event: Event): void => {
    const detail = (event as CustomEvent<{ fetchedAt?: number }>).detail;
    if (typeof detail?.fetchedAt === "number" && detail.fetchedAt > 0) {
      lastQuoteFetchAt = detail.fetchedAt;
    }
    applyHeaderFeedIndicator(host);
  };

  const onSnapshotRefresh = (event: Event): void => {
    const detail = (event as CustomEvent<{ refreshedAt?: string | null }>).detail;
    const nextAt =
      typeof detail?.refreshedAt === "string" && detail.refreshedAt ? detail.refreshedAt : null;
    if (nextAt) {
      rhSnapshotRefreshedAt = nextAt;
      const ringHost = host.querySelector<HTMLElement>("[data-header-feed-ring]");
      if (ringHost) flashRhRefreshCountdownRing(ringHost);
      opts.onRhSyncDue?.();
    } else {
      pollRhContext();
    }
    applyHeaderFeedIndicator(host);
  };

  const onSettingsChanged = (): void => {
    pollRhContext();
  };

  const tick = (): void => {
    applyHeaderFeedIndicator(host);

    const feed = resolveActiveFeed();
    const quoteFetchAt = resolveQuoteFetchAt();
    const quoteRemaining = quoteFetchAt
      ? Math.max(0, Math.ceil((quoteFetchAt + HEADER_FEED_QUOTE_CACHE_MS - Date.now()) / 1000))
      : 0;

    if (quoteRemaining <= 0 && quoteFetchAt > 0 && !quoteRefreshQueued) {
      quoteRefreshQueued = true;
      opts.onQuoteRefreshDue?.();
    }
    if (quoteRemaining > 0) quoteRefreshQueued = false;

    if (feed === "robinhood" && rhFeedConnected()) {
      const sync = currentRhSyncCountdown();
      if (
        sync.enabled &&
        lastSyncRemainingSec !== null &&
        lastSyncRemainingSec > 0 &&
        sync.remainingSec === 0
      ) {
        opts.onRhSyncDue?.();
      }
      lastSyncRemainingSec = sync.remainingSec;

      const pollIntervalMs = sync.remainingSec <= 30 ? 10_000 : 30_000;
      if (
        sync.enabled &&
        sync.remainingSec <= 30 &&
        Date.now() - lastPollAt >= pollIntervalMs &&
        !pollQueued
      ) {
        pollQueued = true;
        lastPollAt = Date.now();
        opts.onRhSnapshotPoll?.() ?? pollRhContext();
        window.setTimeout(() => {
          pollQueued = false;
        }, pollIntervalMs - 500);
      }
    } else {
      lastSyncRemainingSec = null;
    }
  };

  const onRhStatusChanged = (): void => {
    applyHeaderFeedIndicator(host);
  };

  window.addEventListener(HEADER_FEED_QUOTE_REFRESHED_EVENT, onQuoteEvent);
  window.addEventListener(AGENTIC_SNAPSHOT_REFRESHED_EVENT, onSnapshotRefresh);
  window.addEventListener(AGENTIC_SETTINGS_CHANGED_EVENT, onSettingsChanged);
  window.addEventListener("sfe:agentic-status-changed", onRhStatusChanged);

  if (isRobinhoodIntegrationEnabled()) {
    pollRhContext();
    rhPollTimer = window.setInterval(pollRhContext, 30_000);
  }

  tick();
  const tickTimer = window.setInterval(tick, 1000);
  const marketTimer = window.setInterval(() => applyHeaderFeedIndicator(host), 60_000);

  return () => {
    host.removeEventListener("click", onFeedClick);
    window.clearInterval(tickTimer);
    window.clearInterval(marketTimer);
    if (rhPollTimer != null) window.clearInterval(rhPollTimer);
    window.removeEventListener(HEADER_FEED_QUOTE_REFRESHED_EVENT, onQuoteEvent);
    window.removeEventListener(AGENTIC_SNAPSHOT_REFRESHED_EVENT, onSnapshotRefresh);
    window.removeEventListener(AGENTIC_SETTINGS_CHANGED_EVENT, onSettingsChanged);
    window.removeEventListener("sfe:agentic-status-changed", onRhStatusChanged);
  };
}
